import { Col, Divider, Form, Row, Typography } from "antd";
import CustomTable from "components/CustomTable";
import FormInput from "components/FormInput";
import { INPUT_TYPE } from "config/constants";
import { SHIPPING_TYPE } from "features/Cart/constants";
import { t } from "i18next";
import { isEmpty } from "lodash";
import { Fragment, useRef } from "react";
import { formatCurrency } from "utils/helperFuncs";
import Card from "../Card";
import ModalProducts from "../ModalProducts";
import PaymentMethods from "../PaymentMethods";
import SelectProduct from "../SelectProduct/Index";
import "./index.scss";
import useInfoProductAndBill from "./useInfoProductAndBill";

const InfoProductAndBill = ({
  data,
  handleUpdate,
  refetchCart,
  handleUpdateItem,
  handleAddNewItems,
  handleRemoveCartItem,
  handleVatAllItems,
  useInOrder,
  onApplyCashPaymentMethod,
  onCancelApplyCashPaymentMethod,
  loadingApplyCashPaymentMethod,
}) => {
  const { listProducts, exportedWarehouseID, sellerID } = data || {};
  const { columnsProductsInCart, paymentMethodsOptions, bill, billSummary, idsProductsInCart } = useInfoProductAndBill({
    data,
    handleUpdate,
    refetchCart,
    handleUpdateItem,
    handleRemoveCartItem,
    handleVatAllItems,
    useInOrder,
    onApplyCashPaymentMethod,
    onCancelApplyCashPaymentMethod,
    loadingApplyCashPaymentMethod,
  });

  const rightRef = useRef();
  const height = rightRef?.current?.clientHeight;

  // Phí vận chuyển lấy thẳng `cart.shippingFee` do BE tính — giống khối Hoá đơn ở màn Chi tiết đơn
  // (`Order/Detail/components/Bill`). Riêng 3PARTY chưa chọn ĐVVC thì BE chưa chốt được phí, hiện
  // khoảng min-max BE trả sẵn trong `deliver3PartyConfig`.
  const renderShippingFee = () => {
    const deliver3PartyConfig = data?.shippingConfiguration?.deliver3PartyConfig;
    const isDeliver3Party = [SHIPPING_TYPE.DELIVER_3PARTY, SHIPPING_TYPE.DELIVER_3PARTY_INSTANT].includes(
      data?.shippingType
    );

    if (isDeliver3Party && !deliver3PartyConfig?.providerID && !data?.shippingFee) {
      const { carrierMinFee, carrierMaxFee } = deliver3PartyConfig || {};
      return carrierMinFee === carrierMaxFee
        ? formatCurrency(carrierMinFee)
        : `${formatCurrency(carrierMinFee)} - ${formatCurrency(carrierMaxFee)}`;
    }

    return formatCurrency(data?.shippingFee);
  };
  return (
    <Card
      title={
        <Typography.Text strong style={{ color: "#2246DC" }}>
          {t("cart.infoProductAndBill")}
        </Typography.Text>
      }
    >
      <Row gutter={16}>
        <Col span={15}>
          {(isEmpty(useInOrder) || useInOrder?.changeProductInCart) && (
            <Row gutter={16} align="middle" className="add-product-row" style={{ marginBottom: 24 }}>
              <Col span={9}>
                <SelectProduct
                  onChange={(value) => handleAddNewItems([value])}
                  warehouseID={data?.exportedWarehouseID}
                  sellerID={data?.sellerID}
                  handleAddNewItems={handleAddNewItems}
                  idsProductsInCart={idsProductsInCart}
                  exportedWarehouseID={data?.exportedWarehouseID}
                />
              </Col>
              <Col span={6}>
                <ModalProducts
                  handleAddNewItems={handleAddNewItems}
                  idsProductsInCart={idsProductsInCart}
                  warehouseID={exportedWarehouseID}
                  sellerID={sellerID}
                  // Link mặc định của `ModalProducts` có `marginBottom: 24px`. `CustomModal` bọc nó
                  // trong `.button` (display flex) nên margin đó ăn thật, đẩy khối cao hơn ô Select
                  // bên cạnh và phá `align="middle"`.
                  customComponent={<Typography.Link>{t("cart.listProducts")}</Typography.Link>}
                />
              </Col>
            </Row>
          )}

          <Form.List name="items">
            {(item, { remove }) => {
              return (
                <CustomTable
                  className="product-table"
                  columns={columnsProductsInCart}
                  dataSource={listProducts}
                  rowKey={(record) => record.id}
                  scroll={{
                    x: 900,
                    y: +height,
                  }}
                />
              );
            }}
          </Form.List>
        </Col>
        <Col span={9}>
          <Row gutter={[16, 8]} ref={rightRef}>
            {/* <Col span={24}> */}
            <PaymentMethods paymentMethodsOptions={paymentMethodsOptions} data={data} handleUpdate={handleUpdate} />
            {/* </Col> */}
            <Col span={24} className="bill-box">
              <Row gutter={16}>
                <Col span={24}>
                  <Divider orientation="left" className="section-divider" style={{ margin: 0 }}>
                    <Typography.Text style={{ color: "#2246DC" }} strong>
                      {t("cart.bill")}
                    </Typography.Text>
                  </Divider>
                </Col>
                <Col span={24}>
                  <Row gutter={[16, 8]} align="middle">
                    {bill
                      .filter(({ isHidden }) => !isHidden)
                      .map(({ title, value }, index) => (
                        <Fragment key={index}>
                          <Col style={{ textAlign: "end" }} span={12}>
                            <Typography.Text>{title}</Typography.Text>
                          </Col>
                          <Col style={{ textAlign: "end" }} span={12}>
                            {value}
                          </Col>
                        </Fragment>
                      ))}

                    <Col style={{ textAlign: "end" }} span={12}>
                      <Typography.Text>{t("cart.shippingFeeLabel")}</Typography.Text>
                    </Col>
                    <Col style={{ textAlign: "end" }} span={12}>
                      <Typography.Text strong>{renderShippingFee()}</Typography.Text>
                    </Col>

                    <Divider style={{ margin: "6px 0" }} />

                    {billSummary
                      .filter(({ isHidden }) => !isHidden)
                      .map(({ title, value }, index) => (
                        <Fragment key={index}>
                          <Col style={{ textAlign: "end" }} span={12}>
                            <Typography.Text strong>{title}</Typography.Text>
                          </Col>
                          <Col style={{ textAlign: "end" }} span={12}>
                            {value}
                          </Col>
                        </Fragment>
                      ))}
                  </Row>
                </Col>
              </Row>
              {/* </Space> */}
            </Col>
            <Col span={24} className="bill-box">
              <Row gutter={16}>
                <Col span={12}>
                  <FormInput
                    type={INPUT_TYPE.TEXT_AREA}
                    formItemOptions={{
                      name: "internalNote",
                      label: <b>{t("cart.internalNoteLabel")}</b>,
                      labelCol: { span: 24, offset: 0 },
                      style: { marginBottom: 0 },
                    }}
                    inputOptions={{
                      rows: 3,
                      placeholder: t("cart.internalNotePlaceholder"),
                      onBlur: () => handleUpdate(),
                    }}
                  />
                </Col>
                <Col span={12}>
                  <FormInput
                    type={INPUT_TYPE.TEXT_AREA}
                    formItemOptions={{
                      name: "note",
                      label: <b>{t("cart.noteLabel")}</b>,
                      labelCol: { span: 24, offset: 0 },
                      style: { marginBottom: 0 },
                    }}
                    inputOptions={{
                      rows: 3,
                      placeholder: t("cart.notePlaceholder"),
                      onBlur: () => handleUpdate(),
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
};

export default InfoProductAndBill;
